import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, type ReactNode } from 'react';
import { cn, prefersReducedMotion } from '../../lib/utils';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /** Trap focus inside modal. Default: true */
  trapFocus?: boolean;
}

const sizeStyles = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
} as const;

/**
 * Modal with scale-and-fade entrance. Traps focus, restores on close.
 * Uses Radix Dialog under the hood in production; this is a self-contained version.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  actions,
  size = 'md',
}: ModalProps) {
  const reduced = prefersReducedMotion();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={reduced ? false : { opacity: 0 }}
          animate={reduced ? { opacity: 1 } : { opacity: 1 }}
          exit={reduced ? { opacity: 0 } : { opacity: 0 }}
          transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? 'modal-title' : undefined}
          aria-describedby={description ? 'modal-desc' : undefined}
        >
          <motion.div
            className="absolute inset-0 bg-bg-overlay"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          />
          <motion.div
            className={cn(
              'shadow-modal relative w-full rounded-2xl bg-bg-elevated',
              'z-10 p-6',
              sizeStyles[size]
            )}
            initial={reduced ? false : { opacity: 0, scale: 0.92, y: 12 }}
            animate={reduced ? { opacity: 1, scale: 1, y: 0 } : { opacity: 1, scale: 1, y: 0 }}
            exit={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.96, y: 8 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            {title && (
              <h2 id="modal-title" className="mb-1 text-xl font-semibold text-text-primary">
                {title}
              </h2>
            )}
            {description && (
              <p id="modal-desc" className="mb-4 text-sm leading-relaxed text-text-secondary">
                {description}
              </p>
            )}
            {children}
            {actions && <div className="mt-5 flex justify-end gap-2">{actions}</div>}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
